import type { StorageSummary } from "../types/app";

export async function estimateStorage(): Promise<StorageSummary> {
  if (!("storage" in navigator) || !navigator.storage.estimate) {
    return { usage: 0, quota: 0, available: 0, usageRatio: 0, persisted: null };
  }
  const estimate = await navigator.storage.estimate();
  const usage = estimate.usage ?? 0;
  const quota = estimate.quota ?? 0;
  const persisted = navigator.storage.persisted ? await navigator.storage.persisted() : null;
  return {
    usage,
    quota,
    available: Math.max(quota - usage, 0),
    usageRatio: quota > 0 ? usage / quota : 0,
    persisted,
  };
}

export async function requestPersistentStorage(): Promise<boolean | null> {
  if (!("storage" in navigator) || !navigator.storage.persist) return null;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function hasEnoughStorage(bytesToAdd: number, safetyBytes: number): Promise<{ ok: boolean; summary: StorageSummary }> {
  const summary = await estimateStorage();
  if (!summary.quota) return { ok: true, summary };
  return { ok: summary.available - bytesToAdd > safetyBytes, summary };
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}
